import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, BookOpen, Languages, Swords, GraduationCap, BrainCircuit, ArrowRight, Check, BookA, FileSignature, Building2 } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1585314062340-f1a5a7c9328d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const FEATURES = [
  { icon: Sparkles, title: "ECO AI Mentor", desc: "An elite AI that explains any business concept in beginner, intermediate or advanced mode — with real official resources." },
  { icon: BookOpen, title: "Knowledge Universe", desc: "23 categories from Sales to Venture Capital, each with Beginner → Expert lessons generated on demand." },
  { icon: BookA, title: "Business Dictionary", desc: "CAC, LTV, SAFE, Burn Rate and more — each with a simple explanation, real example and mistakes to avoid." },
  { icon: Languages, title: "Patois Translator", desc: "Turn dense business-speak into plain English, or polish plain English into investor-ready language." },
  { icon: Swords, title: "Negotiation Simulator", desc: "Roleplay investor, salary, client and acquisition negotiations against a realistic AI counterpart." },
  { icon: FileSignature, title: "Deal Builder", desc: "Generate deal structures, term checklists, red flags and the exact questions to ask — for any deal type." },
  { icon: GraduationCap, title: "Founder University", desc: "Guided learning paths like Start a Startup and Build a SaaS, with completion tracking." },
  { icon: Building2, title: "Case Study Library", desc: "Deep breakdowns of Airbnb, Stripe, Tesla and more — business models, growth and mistakes." },
  { icon: BrainCircuit, title: "Business IQ Test", desc: "Assess your skill across sales, marketing, finance & negotiation and get a personalized roadmap." },
];

export default function Landing() {
  const { user } = useAuth();
  const cta = user ? "/dashboard" : "/login";

  return (
    <div className="min-h-screen bg-[#050505] text-slate-200 overflow-x-hidden">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-5 md:px-8 py-4">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-[#0055FF] flex items-center justify-center shadow-[0_0_18px_rgba(0,85,255,0.5)]">
              <span className="font-display font-bold text-white text-lg">E</span>
            </div>
            <div className="font-display font-bold text-white tracking-tight text-lg">ECO</div>
          </div>
          <nav className="flex items-center gap-3">
            <Link to="/pricing" className="text-sm text-slate-400 hover:text-white transition-colors hidden sm:block">Pricing</Link>
            <Link to={cta} data-testid="header-cta-btn" className="btn-primary text-sm py-2">{user ? "Dashboard" : "Sign in"}</Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative glow-grid">
        <div className="absolute inset-0 overflow-hidden">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/40 via-[#050505]/80 to-[#050505]" />
        </div>
        <div className="relative max-w-5xl mx-auto px-5 md:px-8 pt-20 md:pt-32 pb-24 text-center">
          <span className="eco-tag inline-flex items-center gap-2 mb-6 font-mono-eco uppercase tracking-widest"><Sparkles size={13} className="text-[#4d8bff]" /> AI-powered business education</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold text-white tracking-tight leading-[1.05]">
            Become a <span className="text-[#4d8bff]">knowledgeable</span><br className="hidden sm:block" /> entrepreneur.
          </h1>
          <p className="mt-6 text-base md:text-lg text-slate-400 max-w-2xl mx-auto">
            The Netflix of business education meets an elite AI mentor. Master sales, marketing, negotiation, finance, investing and startups — from complete beginner to confident founder.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={cta} data-testid="hero-start-btn" className="btn-accent text-base px-8 py-3.5">Start learning free <ArrowRight size={18} /></Link>
            <Link to="/pricing" data-testid="hero-pricing-btn" className="btn-secondary text-base px-8 py-3.5">See Pro plans</Link>
          </div>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-slate-500">
            {["No credit card to start", "Beginner → Expert", "Real official resources"].map((t) => (
              <span key={t} className="flex items-center gap-2"><Check size={15} className="text-emerald-400" /> {t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Features bento */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20">
        <div className="max-w-2xl mb-12">
          <span className="font-mono-eco text-xs uppercase tracking-widest text-slate-500">The platform</span>
          <h2 className="font-display text-3xl md:text-4xl font-semibold text-white tracking-tight mt-3">Everything you need to think like a founder.</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f, i) => (
            <div key={f.title} className="eco-card eco-card-hover p-7" data-testid={`feature-card-${i}`}>
              <div className="h-11 w-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mb-5">
                <f.icon size={22} className="text-[#4d8bff]" strokeWidth={1.5} />
              </div>
              <h3 className="font-display text-lg font-semibold text-white mb-2">{f.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 pb-24">
        <div className="eco-card glow-grid p-10 md:p-16 text-center">
          <h2 className="font-display text-3xl md:text-5xl font-bold text-white tracking-tight">Your business education<br /> starts now.</h2>
          <p className="mt-4 text-slate-400 max-w-xl mx-auto">Ask ECO anything. Take the Business IQ test. Build your first deal. It's all here.</p>
          <Link to={cta} data-testid="cta-bottom-btn" className="btn-accent text-base px-8 py-3.5 mt-8 inline-flex">Get started <ArrowRight size={18} /></Link>
        </div>
      </section>

      <footer className="border-t border-white/5 py-8 text-center">
        <p className="text-sm text-slate-600">© 2026 Entrepreneur OS (ECO). Built for the next generation of founders.</p>
        <div className="mt-3 flex items-center justify-center gap-5 text-sm">
          <Link to="/terms" data-testid="footer-terms" className="text-slate-500 hover:text-white transition-colors">Terms of Service</Link>
          <Link to="/privacy" data-testid="footer-privacy" className="text-slate-500 hover:text-white transition-colors">Privacy Policy</Link>
          <a href="mailto:ecoentrepreneuros@gmail.com" className="text-slate-500 hover:text-white transition-colors">Contact</a>
        </div>
      </footer>
    </div>
  );
}
